/**
 * Annotation Renderer
 * 
 * Renders the visual content of each annotation type.
 * Title Box uses the existing TitleBoxData format for consistency with exports.
 */

import React from 'react';
import { Box, Typography, Divider, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type {
  PageAnnotation,
  TitleBoxAnnotation,
  NorthArrowAnnotation,
  LegendAnnotation,
  ManifestAnnotation,
  NotesAnnotation,
  ScaleBarAnnotation,
} from '../../types/annotations';
import { SPACING_REQUIREMENTS } from '../../types/annotations';

interface AnnotationRendererProps {
  annotation: PageAnnotation;
}

// Title Box Renderer - Matches the backend export format exactly
const TitleBoxRenderer: React.FC<{ data: TitleBoxAnnotation }> = ({ data }) => {
  if (!data.useTitleBox) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          border: '1px dashed #9CA3AF',
          backgroundColor: 'rgba(0,0,0,0.02)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 10, color: '#9CA3AF' }}>Title Box Disabled</Typography>
      </Box>
    );
  }

  const stateData = SPACING_REQUIREMENTS[data.state as keyof typeof SPACING_REQUIREMENTS] || SPACING_REQUIREMENTS.SA;
  const speeds = stateData.speeds.map(String);
  const selectedSpeed = String(data.roadSpeed).trim();
  const highlightIdx = speeds.findIndex(s => s.trim() === selectedSpeed);

  // Check visibility flags
  const anyLogoVisible = data.showLogoLeft || data.showLogoCenter || data.showLogoRight;

  // Fixed section widths: Logo 15%, Metadata 40%, Table 35%
  const sectionCount = [anyLogoVisible, data.showMetadataSection, data.showSpacingTable].filter(Boolean).length;

  const logoWidth = anyLogoVisible
    ? (sectionCount === 1 ? '100%' : '15%')
    : '0%';
  const infoWidth = data.showMetadataSection
    ? (sectionCount === 1 ? '100%' : '40%')
    : '0%';
  const tableWidth = data.showSpacingTable
    ? (sectionCount === 1 ? '100%' : '35%')
    : '0%';

  const metadataRows = [
    { label: 'TGS: ', value: data.tcpNumber },
    { label: 'Client: ', value: data.client },
    { label: 'Contact: ', value: data.contact },
    { label: 'Phone: ', value: data.phone },
    { label: 'Location: ', value: data.location },
    { label: 'Council: ', value: data.council },
    { label: 'Start date: ', value: data.startDate },
    { label: 'Finish date: ', value: data.finishDate },
    { label: 'Start time: ', value: data.startTime },
    { label: 'Finish time: ', value: data.finishTime },
    { label: 'Author: ', value: data.author },
    { label: 'Created on: ', value: data.createdOn },
    { label: 'Modified by: ', value: data.modifiedBy },
    { label: 'Modified on: ', value: data.modifiedOn },
  ];

  const tableRows = [
    { label: 'Length of zone (m)', values: Array(5).fill(data.lengthOfZone || '—') },
    { label: 'Buffer zone (m)', values: stateData.bufferZone as string[] },
    { label: 'Advance Warning (m)', values: stateData.advanceWarning as string[] },
    { label: 'Taper area (m)', values: stateData.taperArea as string[] },
    { label: 'Safety Buffer (m)', values: stateData.safetyBuffer as string[] },
    { label: 'Termination Area (m)', values: stateData.terminationArea as string[] },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        border: '2px solid #1f1f1f',
        backgroundColor: '#f2f5d3',
        display: 'flex',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* LOGO SECTION - Yellow-green background */}
      {anyLogoVisible && (
        <Box
          sx={{
            width: logoWidth,
            backgroundColor: '#d6e96f',
            borderRight: '2px solid #1f1f1f',
            display: 'flex',
            flexDirection: 'column',
            p: 1,
            gap: 0.5,
          }}
        >
          {/* Logo slots */}
          <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
            {(['left', 'center', 'right'] as const)
              .filter((pos) => (pos === 'left' && data.showLogoLeft) || (pos === 'center' && data.showLogoCenter) || (pos === 'right' && data.showLogoRight))
              .map((position) => (
                <Box
                  key={position}
                  sx={{
                    flex: 1,
                    border: '2px solid #1f1f1f',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 40,
                  }}
                >
                  {data.logos[position] ? (
                    <Box
                      component="img"
                      src={data.logos[position]!}
                      sx={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: '#111' }}>LOGO</Typography>
                  )}
                </Box>
              ))}
          </Box>
        </Box>
      )}

      {/* INFO/METADATA SECTION - Table-like with row borders */}
      {data.showMetadataSection && (() => {
        const leftRows = metadataRows.slice(0, 7);
        const rightRows = metadataRows.slice(7);
        const maxRows = Math.max(leftRows.length, rightRows.length);

        const renderColumn = (rows: typeof metadataRows, colIdx: number) => (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: colIdx === 0 ? '1px solid #1f1f1f' : 'none' }}>
            {Array.from({ length: maxRows }).map((_, idx) => {
              const row = rows[idx];
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: idx < maxRows - 1 ? '1px solid #c8c8c8' : 'none',
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: '38%',
                      flexShrink: 0,
                      px: '3px',
                      py: '1px',
                      borderRight: '1px solid #c8c8c8',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#f5f5e8',
                    }}
                  >
                    <Typography sx={{ fontSize: 6.5, color: '#333', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {row ? row.label : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, px: '3px', py: '1px', overflow: 'hidden' }}>
                    <Typography sx={{ fontSize: 6.5, color: '#111', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row ? (row.value || '—') : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );

        return (
          <Box
            sx={{
              width: infoWidth,
              backgroundColor: '#fcfcf2',
              borderRight: data.showSpacingTable ? '2px solid #1f1f1f' : 'none',
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            {renderColumn(leftRows, 0)}
            {renderColumn(rightRows, 1)}
          </Box>
        );
      })()}

      {/* SPACING TABLE SECTION */}
      {data.showSpacingTable && (
        <Box
          sx={{
            width: tableWidth,
            backgroundColor: '#f2f5d3',
            p: 0.5,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {/* Table Header - Yellow */}
          <Box
            sx={{
              backgroundColor: '#ffe75d',
              border: '2px solid #1f1f1f',
              p: 0.25,
              textAlign: 'center',
              mb: 0.25,
            }}
          >
            <Typography sx={{ fontSize: 8, fontWeight: 700, color: '#111' }}>
              {data.roadType?.toUpperCase() || 'TWO WAY - TWO LANE ROADS'}
            </Typography>
          </Box>

          {/* Speed Header Row */}
          <Box sx={{ display: 'flex', border: '1px solid #1f1f1f', backgroundColor: '#d8d8d8' }}>
            <Box sx={{ flex: '0 0 28%', p: '2px 3px', borderRight: '1px solid #1f1f1f', minWidth: 0 }}>
              <Typography sx={{ fontSize: 6, fontWeight: 600, color: '#111', whiteSpace: 'nowrap' }}>Road speed (km/h)</Typography>
            </Box>
            {speeds.map((speed, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  p: '2px 2px',
                  textAlign: 'center',
                  borderRight: i < speeds.length - 1 ? '1px solid #1f1f1f' : 'none',
                  backgroundColor: i === highlightIdx ? '#fff4b5' : 'transparent',
                  minWidth: 0,
                }}
              >
                <Typography sx={{ fontSize: 6, fontWeight: 600, color: '#111' }}>{speed}</Typography>
              </Box>
            ))}
          </Box>

          {/* Data Rows */}
          {tableRows.map((row, rowIdx) => (
            <Box
              key={rowIdx}
              sx={{
                display: 'flex',
                border: '1px solid #1f1f1f',
                borderTop: 'none',
                backgroundColor: 'white',
              }}
            >
              <Box sx={{ flex: '0 0 28%', p: '2px 3px', borderRight: '1px solid #1f1f1f', minWidth: 0 }}>
                <Typography sx={{ fontSize: 5.5, color: '#111', whiteSpace: 'nowrap' }}>{row.label}</Typography>
              </Box>
              {row.values.map((val, i) => {
                const customKey = `${data.state}_${['lengthOfZone', 'bufferZone', 'advanceWarning', 'taperArea', 'safetyBuffer', 'terminationArea'][rowIdx]}_${i}`;
                const displayValue = data.customSpacingRequirements?.[customKey] || val;
                return (
                  <Box
                    key={i}
                    sx={{
                      flex: 1,
                      p: '1px 2px',
                      textAlign: 'center',
                      borderRight: i < row.values.length - 1 ? '1px solid #1f1f1f' : 'none',
                      backgroundColor: i === highlightIdx ? '#fff4b5' : 'transparent',
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography sx={{ fontSize: 5.5, color: '#111', lineHeight: 1.2 }}>{displayValue}</Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// North Arrow Renderer
const NorthArrowRenderer: React.FC<{ data: NorthArrowAnnotation }> = ({ data }) => {
  const { style, rotation, width, height } = data;
  const size = Math.min(width, height);

  const renderArrow = () => {
    switch (style) {
      case 'star':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100">
            <g transform={`rotate(${rotation} 50 50)`}>
              <polygon points="50,5 54,40 90,50 54,60 50,95 46,60 10,50 46,40" fill="#000000" stroke="#333333" strokeWidth="1" />
              <text x="50" y="25" textAnchor="middle" fontSize="14" fontWeight="bold" fill="white">N</text>
              <circle cx="50" cy="50" r="8" fill="#000000" stroke="#333333" strokeWidth="1" />
            </g>
          </svg>
        );
      case 'compass':
        // New compass navigator icon
        return (
          <svg width={size} height={size} viewBox="0 0 512 497.35">
            <g transform={`rotate(${rotation} 256 248.675)`}>
              <path d="M265.27 59.18c49.11 0 93.84 18.81 127.36 49.61l24.57-13.96-13.84 24.64c31.17 33.6 50.22 78.59 50.22 128.02 0 48.77-18.54 93.22-48.96 126.66l13.8 24.27-24.25-13.65c-33.69 31.64-79.04 51.03-128.9 51.03-49.35 0-94.26-18.98-127.84-50.05l-25.32 14.41 14.28-25.49c-30.7-33.51-49.43-78.16-49.43-127.18 0-49.92 19.43-95.3 51.14-129l-13.4-23.55 23.74 13.36c33.46-30.51 77.98-49.12 126.83-49.12zM258.1 0l21.88 27.53V8.45c0-2.7-.38-4.5-1.15-5.45-1.05-1.29-2.81-1.9-5.28-1.86V0h14.68v1.14c-1.88.25-3.14.56-3.79.94-.63.39-1.14 1.01-1.51 1.89-.36.87-.53 2.37-.53 4.48v37h-1.11l-30-37.05v28.34c0 2.57.58 4.31 1.75 5.22 1.18.9 2.53 1.35 4.04 1.35h1.05v1.14h-15.82v-1.14c2.46-.02 4.16-.53 5.12-1.52.97-1 1.45-2.67 1.45-5.05V5.31l-.93-1.15c-.94-1.21-1.77-2-2.49-2.38-.72-.38-1.76-.6-3.11-.64V0h15.75zm19.64 450.89.33 14.82h-1.34c-.63-3.71-2.18-6.69-4.67-8.95-2.49-2.26-5.17-3.39-8.05-3.39-2.24 0-4 .59-5.3 1.79-1.3 1.18-1.95 2.56-1.95 4.1 0 .98.23 1.85.69 2.62.63 1.02 1.64 2.04 3.05 3.04 1.03.72 3.4 2 7.11 3.84 5.2 2.54 8.71 4.96 10.52 7.22 1.8 2.27 2.69 4.87 2.69 7.79 0 3.71-1.45 6.9-4.34 9.57-2.9 2.67-6.57 4.01-11.03 4.01-1.4 0-2.73-.15-3.97-.43-1.25-.28-2.81-.81-4.69-1.61-1.05-.43-1.91-.64-2.59-.64-.57 0-1.17.22-1.8.66-.64.44-1.15 1.11-1.54 2.02h-1.14v-16.76h1.14c.96 4.7 2.81 8.3 5.55 10.77 2.75 2.47 5.7 3.71 8.87 3.71 2.45 0 4.4-.67 5.86-2 1.45-1.33 2.17-2.87 2.17-4.65 0-1.04-.27-2.05-.83-3.04-.56-.98-1.4-1.91-2.54-2.8-1.14-.88-3.15-2.03-6.03-3.45-4.05-1.98-6.95-3.68-8.72-5.08-1.75-1.39-3.1-2.96-4.05-4.68-.95-1.73-1.42-3.63-1.42-5.7 0-3.54 1.29-6.55 3.9-9.05 2.59-2.49 5.86-3.73 9.82-3.73 1.44 0 2.84.17 4.19.51 1.02.27 2.28.75 3.75 1.46 1.48.7 2.5 1.05 3.1 1.05.57 0 1.01-.17 1.34-.52.32-.34.64-1.18.91-2.5h1.01zm210.79-223.07v18.1h.89c2.83 0 4.87-.89 6.14-2.65 1.27-1.77 2.08-4.37 2.43-7.81h1.27v23.26h-1.27c-.26-2.53-.82-4.61-1.65-6.22-.85-1.62-1.83-2.71-2.94-3.27-1.12-.55-2.74-.83-4.87-.83v12.49c0 2.45.11 3.93.32 4.48.2.55.59 1 1.16 1.34.57.35 1.49.53 2.75.53h2.66c4.14 0 7.47-.97 9.98-2.88 2.5-1.91 4.3-4.82 5.38-8.76H512l-2 14.12h-38.36v-1.14h1.46c1.29 0 2.33-.24 3.11-.7.57-.31 1.01-.83 1.31-1.58.23-.53.36-1.91.36-4.16v-29.3c0-2.02-.06-3.26-.17-3.73-.21-.77-.61-1.36-1.21-1.77-.82-.62-1.95-.93-3.4-.93h-1.46v-1.14h37.14v13.14h-1.27c-.63-3.22-1.52-5.52-2.66-6.92-1.14-1.4-2.75-2.42-4.84-3.08-1.22-.39-3.5-.59-6.85-.59h-4.63zm-422.69-3.06v1.14c-.82.07-1.53.31-2.1.71-.57.41-1.07 1.03-1.52 1.85-.12.27-.8 1.97-2.03 5.1l-13.91 36.66h-1.2l-10.99-28.93-12.18 28.93h-1.16L6.09 233.94c-1.49-3.68-2.52-5.91-3.11-6.68-.59-.77-1.58-1.22-2.98-1.36v-1.14h19.25v1.14c-1.55.05-2.58.28-3.09.72-.52.43-.78.96-.78 1.6 0 .84.54 2.61 1.61 5.33l8.71 21.56 7.05-17.03-1.77-4.59c-1-2.58-1.78-4.31-2.32-5.17-.55-.86-1.18-1.49-1.89-1.85-.7-.38-1.74-.57-3.12-.57v-1.14h21.34v1.14c-1.5.03-2.58.15-3.24.38-.46.14-.81.41-1.07.77-.27.36-.39.78-.39 1.24 0 .51.48 2.06 1.46 4.67l8.08 21.03 7.23-19.03c.76-1.98 1.23-3.34 1.41-4.07.18-.73.26-1.4.26-2.01 0-.91-.31-1.61-.92-2.14-.6-.51-1.78-.79-3.5-.84v-1.14h11.53zm160.82 85.13-56.83 32.33 33.24-59.05 17.57 4.83-122.08-33.56c1.77 43.32 20.05 82.38 48.72 111.04 28.1 28.11 66.21 46.23 108.52 48.6l-29.14-104.19zm-23.29-101.85-31.93-56.13 56.1 31.58 28.2-102.58c-42.29 2.38-80.37 20.5-108.46 48.59-27.95 27.95-46.03 65.78-48.56 107.81l123.52-34.54-18.87 5.27zm99.06-23.43 57.62-32.78-32.02 56.9-20.43-5.62 124.21 34.14c-2.55-42.01-20.61-79.81-48.55-107.75-28.4-28.4-67.01-46.61-109.85-48.66l29.02 103.77zm25.58 98.85 32.81 57.67-58.27-32.8-29.08 105.82c42.82-2.08 81.4-20.28 109.79-48.67 28.68-28.68 46.96-67.76 48.72-111.1l-103.97 29.08zm-63.79-37.51.39.41-36.72 39.27-144.36-39.68h180.69zm2.1-.17-.4-.4 11.49-12.29-13.29 12.78-39.27-36.72 39.68-144.36v180.63l13.19-12.65 24.92-26.66L447 245.78H266.32zm-1.22 3.33 39.27 36.72-35.31 128.47-4.37 15.89V249.56l.41-.45z" fill="#000000"/>
            </g>
          </svg>
        );
      case 'simple':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100">
            <g transform={`rotate(${rotation} 50 50)`}>
              <line x1="50" y1="90" x2="50" y2="10" stroke="#000000" strokeWidth="3" />
              <polygon points="50,10 40,30 60,30" fill="#000000" />
              <text x="50" y="98" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#000000">N</text>
            </g>
          </svg>
        );
      case 'arrow':
      default:
        return (
          <svg width={size} height={size} viewBox="0 0 100 100">
            <g transform={`rotate(${rotation} 50 50)`}>
              <polygon points="50,5 65,45 55,45 55,90 45,90 45,45 35,45" fill="#000000" stroke="#333333" strokeWidth="1" />
              <text x="50" y="35" textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">N</text>
            </g>
          </svg>
        );
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {renderArrow()}
    </Box>
  );
};

// Legend Renderer
const LegendRenderer: React.FC<{ data: LegendAnnotation }> = ({ data }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      border: '2px solid #000000',
      backgroundColor: 'white',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box sx={{ backgroundColor: '#000000', color: 'white', px: 1.5, py: 0.75 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 12, letterSpacing: 0.5 }}>{data.title}</Typography>
    </Box>
    <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
      {data.entries.length === 0 ? (
        <Typography sx={{ fontSize: 10, color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
          No items in plan
        </Typography>
      ) : (
        data.entries.map((entry, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: idx < data.entries.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#E5E7EB', borderRadius: 1, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 10, flex: 1, color: '#000000' }}>{entry.label}</Typography>
            {data.showCounts && (
              <Typography sx={{ fontSize: 10, color: '#333333', fontWeight: 600 }}>×{entry.count}</Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  </Box>
);

// Manifest Renderer
const ManifestRenderer: React.FC<{ data: ManifestAnnotation }> = ({ data }) => {
  const total = data.entries.reduce((sum, e) => sum + e.quantity, 0);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        border: '2px solid #000000',
        backgroundColor: 'white',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ backgroundColor: '#000000', color: 'white', px: 1.5, py: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 12, letterSpacing: 0.5 }}>{data.title}</Typography>
      </Box>
      <Box sx={{ display: 'flex', backgroundColor: '#F3F4F6', px: 1, py: 0.5, borderBottom: '1px solid #E5E7EB' }}>
        <Typography sx={{ flex: 1, fontSize: 9, fontWeight: 600, color: '#333333' }}>ITEM</Typography>
        <Typography sx={{ width: 40, fontSize: 9, fontWeight: 600, color: '#333333', textAlign: 'right' }}>QTY</Typography>
        <Typography sx={{ width: 30, fontSize: 9, fontWeight: 600, color: '#333333', textAlign: 'center' }}>UNIT</Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {data.entries.length === 0 ? (
          <Typography sx={{ fontSize: 10, color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
            No equipment in plan
          </Typography>
        ) : (
          data.entries.map((entry, idx) => (
            <Box key={idx} sx={{ display: 'flex', px: 1, py: 0.5, borderBottom: '1px solid #F3F4F6' }}>
              <Typography sx={{ flex: 1, fontSize: 10, color: '#000000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.item}</Typography>
              <Typography sx={{ width: 40, fontSize: 10, fontWeight: 600, color: '#000000', textAlign: 'right' }}>{entry.quantity}</Typography>
              <Typography sx={{ width: 30, fontSize: 10, color: '#333333', textAlign: 'center' }}>{entry.unit}</Typography>
            </Box>
          ))
        )}
      </Box>
      {data.showTotal && data.entries.length > 0 && (
        <Box sx={{ display: 'flex', backgroundColor: '#F3F4F6', px: 1, py: 0.5, borderTop: '1px solid #E5E7EB' }}>
          <Typography sx={{ flex: 1, fontSize: 10, fontWeight: 600, color: '#000000' }}>Total</Typography>
          <Typography sx={{ width: 40, fontSize: 10, fontWeight: 700, color: '#000000', textAlign: 'right' }}>{total}</Typography>
          <Typography sx={{ width: 30 }} />
        </Box>
      )}
    </Box>
  );
};

// Notes Renderer
const NotesRenderer: React.FC<{ data: NotesAnnotation }> = ({ data }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      border: data.showBorder ? `2px solid ${data.borderColor}` : 'none',
      backgroundColor: data.backgroundColor,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {data.title && (
      <Box sx={{ backgroundColor: data.borderColor, color: 'white', px: 1.5, py: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.5 }}>{data.title}</Typography>
      </Box>
    )}
    <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto' }}>
      <Typography
        sx={{
          fontSize: data.fontSize,
          color: data.textColor,
          whiteSpace: 'pre-wrap',
          lineHeight: 1.4,
        }}
      >
        {data.content || 'Enter notes here...'}
      </Typography>
    </Box>
  </Box>
);

// Scale Bar Renderer
const ScaleBarRenderer: React.FC<{ data: ScaleBarAnnotation }> = ({ data }) => {
  const { scale, length, units, style } = data;
  const divisions = 5;
  const divisionWidth = length / divisions;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, mb: 0.5, color: '#000000' }}>{scale}</Typography>
      <Box sx={{ display: 'flex', height: 8 }}>
        {Array.from({ length: divisions }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: divisionWidth,
              height: '100%',
              backgroundColor: style === 'alternating' 
                ? (i % 2 === 0 ? '#000000' : 'white')
                : '#000000',
              border: '1px solid #000000',
              borderLeft: i === 0 ? '1px solid #000000' : 'none',
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', width: length, justifyContent: 'space-between', mt: 0.25 }}>
        <Typography sx={{ fontSize: 8, color: '#000000' }}>0</Typography>
        <Typography sx={{ fontSize: 8, color: '#000000' }}>{units === 'metric' ? `${length}m` : `${length}ft`}</Typography>
      </Box>
    </Box>
  );
};

export const AnnotationRenderer: React.FC<AnnotationRendererProps> = ({ annotation }) => {
  switch (annotation.type) {
    case 'title-box':
      return <TitleBoxRenderer data={annotation as TitleBoxAnnotation} />;
    case 'north-arrow':
      return <NorthArrowRenderer data={annotation as NorthArrowAnnotation} />;
    case 'legend':
      return <LegendRenderer data={annotation as LegendAnnotation} />;
    case 'manifest':
      return <ManifestRenderer data={annotation as ManifestAnnotation} />;
    case 'notes':
      return <NotesRenderer data={annotation as NotesAnnotation} />;
    case 'scale-bar':
      return <ScaleBarRenderer data={annotation as ScaleBarAnnotation} />;
    default:
      return (
        <Box sx={{ width: '100%', height: '100%', backgroundColor: '#F3F4F6', border: '1px dashed #9CA3AF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 10, color: '#6B7280' }}>Unknown annotation</Typography>
        </Box>
      );
  }
};

export default AnnotationRenderer;
